import type { StackFrame } from "@sentry/browser";
import {
  ERROR_TYPE_ONUNHANDLEDREJECTION,
  ERROR_MESSAGE_PROMISE_REJECTION_EMPTY_VALUE,
  ERROR_MESSAGE_PROMISE_REJECTION_NULL_VALUE,
  ERROR_MESSAGE_PROMISE_REJECTION_VALUE_WITH_ID,
} from "./constants";

/**
 * Filter out CSP/CORS font-src, img-src and connect-src violations from
 * third-party domains. These are noise from browser extensions or third-party
 * scripts and we deliberately keep them out of Sentry. (Genuine failed GraphQL
 * fetches surface as `TypeError: Load failed` via the Apollo onError link, not
 * as connect-src violations, so they are unaffected by this filter.)
 */
export const isCorsFontAndImgError = (errorMessage: string): boolean =>
  /Blocked.*font/i.test(errorMessage) ||
  errorMessage.includes("Blocked 'font'") ||
  /font-src/i.test(errorMessage) ||
  /img-src/i.test(errorMessage) ||
  /connect-src/i.test(errorMessage) ||
  /static\.shopback\.com/i.test(errorMessage);

const isGraphQLResponseError = (errorMessage: string): boolean =>
  /Sorry, .* is now out of stock and was removed from your cart\./i.test(errorMessage) ||
  /Sorry, subscriptions require a minimum of 3 wines\./i.test(errorMessage) ||
  /Invalid or expired code\./i.test(errorMessage);

/**
 * Filter out Iterable SDK errors (400/401/409 status codes).
 * These occur when JWT token is not yet available, user is unauthorized, or API conflicts.
 * Error messages match Axios format: "Request failed with status code XXX"
 */
export const isIterableApiError = (errorMessage: string): boolean =>
  /Request failed with status code (400|401|409)/i.test(errorMessage) ||
  /Error updating user in Iterable/i.test(errorMessage) ||
  /Error tracking: '.*' event in Iterable\..*\{"message":"Network Error","name":"AxiosError"/i.test(
    errorMessage
  );

/**
 * Filter out unhandled promise rejection errors with specific values.
 * These are captured by Sentry but are not actionable.
 * @see https://github.com/getsentry/sentry-javascript/issues/3440
 */
export const isUnhandledPromiseRejectionError = (
  mechanismType: string | undefined,
  errorValue: string | undefined
): boolean =>
  mechanismType === ERROR_TYPE_ONUNHANDLEDREJECTION &&
  !!errorValue &&
  (`${errorValue}` === ERROR_MESSAGE_PROMISE_REJECTION_EMPTY_VALUE ||
    `${errorValue}` === ERROR_MESSAGE_PROMISE_REJECTION_NULL_VALUE ||
    !!errorValue.match(RegExp(ERROR_MESSAGE_PROMISE_REJECTION_VALUE_WITH_ID, "i")));

export const isGhostApiError = (errorMessage: string): boolean =>
  /Request not understood error, cannot list posts\./i.test(errorMessage) ||
  /Request not understood error, cannot read post\./i.test(errorMessage);

export const isWebKitBridgeError = (errorMessage: string): boolean =>
  /window\.webkit\.messageHandlers/i.test(errorMessage) || /webkit\.messageHandlers/i.test(errorMessage);

/**
 * Android counterpart of isWebKitBridgeError. Instagram's in-app browser
 * injects scripts under `iabjs://` (e.g. `navigation_performance_logger_android`)
 * that post to a Java object the Instagram app exposes to the WebView; once the
 * app tears that object down, Chromium throws "Error invoking postMessage: Java
 * object is gone". Our own Capacitor Android app can throw the same message
 * (its `androidBridge.postMessage` can be a Java object too), so only drop it
 * when the innermost frame — Sentry orders frames oldest-first — is an
 * injected `iabjs://` script.
 */
export const isAndroidJavaBridgeError = (errorMessage: string, frames: StackFrame[] | undefined): boolean =>
  /Java object is gone/i.test(errorMessage) &&
  /^iabjs:\/\//i.test(frames?.[frames.length - 1]?.filename || "");

/**
 * Determines if a Sentry error event should be filtered out (not sent).
 * Returns true if the error should be dropped.
 */
export const shouldFilterSentryError = (
  errorMessage: string,
  mechanismType: string | undefined,
  frames?: StackFrame[]
): boolean =>
  isCorsFontAndImgError(errorMessage) ||
  isIterableApiError(errorMessage) ||
  isUnhandledPromiseRejectionError(mechanismType, errorMessage) ||
  isGhostApiError(errorMessage) ||
  isGraphQLResponseError(errorMessage) ||
  isWebKitBridgeError(errorMessage) ||
  isAndroidJavaBridgeError(errorMessage, frames);
